import {
	RiAddLine,
	RiEyeFill,
	RiFlagFill,
	RiLiveFill,
	RiMicFill,
} from "@remixicon/react";
import { formatSetTimeWithPreferences } from "@workspace/shared-utils";
import type { ProcessedSet } from "@workspace/types";
import {
	getB2BDisplayParts,
	SET_STATUSES,
	type SetStatus,
} from "@workspace/types";
import { cn } from "@workspace/ui/lib/utils";
import { Fragment, type ReactElement } from "react";
import { Facepile } from "@/components/shared/facepile";
// Set rendering logic - extracted from set-card-helpers.tsx
import { StarRating } from "@/components/shared/star-rating";
import { isSetPast } from "@/lib/utils/date/formatting";

// ===== BUTTON CONTENT RENDERING =====

/**
 * Gets button content with icons and responsive text based on status
 */
export function getSetButtonContent(
	status: SetStatus,
	set?: ProcessedSet,
	responsive = true,
): ReactElement {
	const iconSize = "w-4 h-4";

	switch (status) {
		case SET_STATUSES.SAW_LIVE:
			return (
				<>
					<RiMicFill className={iconSize} />
					{responsive ? (
						<>
							<span className="hidden sm:inline">Saw Live</span>
							<span className="sm:hidden">Live</span>
						</>
					) : (
						"Saw Live"
					)}
				</>
			);
		case SET_STATUSES.SAW_STREAM:
			return (
				<>
					<RiLiveFill className={iconSize} />
					{responsive ? (
						<>
							<span className="hidden sm:inline">Saw Stream</span>
							<span className="sm:hidden">Stream</span>
						</>
					) : (
						"Saw Stream"
					)}
				</>
			);
		case SET_STATUSES.MUST_SEE:
			return (
				<>
					<RiFlagFill className={iconSize} />
					{responsive ? (
						<>
							<span className="hidden sm:inline">Must See</span>
							<span className="sm:hidden">Must</span>
						</>
					) : (
						"Must See"
					)}
				</>
			);
		case SET_STATUSES.SCHEDULED: {
			const isPast = set ? isSetPast(set) : false;
			return (
				<>
					<RiEyeFill className={iconSize} />
					{responsive ? (
						<>
							<span className="hidden sm:inline">
								{isPast ? "Wanted to See" : "Want to See"}
							</span>
							<span className="sm:hidden">{isPast ? "Wanted" : "Want"}</span>
						</>
					) : isPast ? (
						"Wanted to See"
					) : (
						"Want to See"
					)}
				</>
			);
		}
		default:
			return (
				<>
					<RiAddLine className={iconSize} />
					{responsive ? (
						<>
							<span className="hidden sm:inline">Add to List</span>
							<span className="sm:hidden">Add</span>
						</>
					) : (
						"Add to List"
					)}
				</>
			);
	}
}

// ===== STAR RATING UTILITIES =====

export interface StarRatingProps {
	rating: number;
	size?: "sm" | "md" | "lg";
	className?: string;
}

/**
 * Renders star rating component using the shared StarRating component
 */
export function renderStarRating({
	rating,
	size = "md",
	className,
}: StarRatingProps): ReactElement {
	return (
		<StarRating
			rating={rating}
			size={size}
			className={className}
			readOnly={true}
		/>
	);
}

// ===== ARTIST NAME RENDERING =====

/**
 * Gets the display name for a set as a plain string (used for exports and text-only contexts)
 */
export function getSetDisplayName(set: ProcessedSet): string {
	// For surprise sets, return placeholder
	if (set.is_surprise) {
		return set.placeholder_name || "Surprise Guest";
	}

	const displayParts = getB2BDisplayParts(set);

	// For sets with multiple parts (3+ artists)
	if (displayParts.parts && displayParts.parts.length > 0) {
		return displayParts.parts.map((part) => part.text).join(" ");
	}

	// For 2-artist B2B, presents sets (connector + secondaryText)
	if (displayParts.connector && displayParts.secondaryText) {
		return `${displayParts.mainText} ${displayParts.connector} ${displayParts.secondaryText}`;
	}

	// For "& Friends" sets (connector but no secondaryText)
	if (displayParts.connector) {
		return `${displayParts.mainText} ${displayParts.connector}`;
	}

	// For single artist sets
	return displayParts.mainText;
}

export interface ArtistNameProps {
	set: ProcessedSet;
	className?: string;
	truncateMode?: "none" | "truncate" | "line-clamp-2";
	textBreaking?: "break-words" | "whitespace-nowrap";
}

/**
 * Renders artist name with proper B2B/presents/friends formatting
 */
export function renderArtistName({
	set,
	className,
	truncateMode = "none",
	textBreaking = "break-words",
}: ArtistNameProps): ReactElement {
	const displayParts = getB2BDisplayParts(set);

	const truncateClass =
		truncateMode === "truncate"
			? "truncate"
			: truncateMode === "line-clamp-2"
				? "line-clamp-2"
				: "";

	const content = (
		<>
			{displayParts.isB2B ||
			displayParts.isPresents ||
			displayParts.isFriends ? (
				displayParts.parts ? (
					// For 3+ artist collaborations, render each part individually with spacing
					displayParts.parts.map((part, index) => (
						<Fragment key={index}>
							{part.type === "artist" ? (
								<span className={textBreaking}>{part.text}</span>
							) : part.type === "presents" ? (
								<span className={textBreaking}>{part.text}</span>
							) : (
								<span
									className={cn(
										"font-normal text-muted-foreground/50",
										textBreaking,
									)}
								>
									{part.text}
								</span>
							)}
							{index < (displayParts.parts?.length ?? 0) - 1 && " "}
						</Fragment>
					))
				) : (
					// For 2-artist collaborations, presents, and friends sets
					<>
						<span className={textBreaking}>{displayParts.mainText}</span>
						{displayParts.connector && (
							<span
								className={cn(
									"mx-0.5 font-normal text-muted-foreground/50",
									textBreaking,
								)}
							>
								{displayParts.connector}
							</span>
						)}
						{displayParts.secondaryText && (
							<span className={textBreaking}>{displayParts.secondaryText}</span>
						)}
					</>
				)
			) : (
				<span className={textBreaking}>{displayParts.mainText}</span>
			)}
			{/* Add special guests if present */}
			{displayParts.specialGuests && (
				<>
					<span
						className={cn(
							"mx-1 font-normal text-muted-foreground/50",
							textBreaking,
						)}
					>
						w/
					</span>
					<span className={textBreaking}>{displayParts.specialGuests}</span>
				</>
			)}
		</>
	);

	return <div className={cn(truncateClass, className)}>{content}</div>;
}

// ===== SOCIAL INTERACTIONS UTILITIES =====

export interface FacepileProps {
	socialInteractions?: Array<{
		id: string;
		username: string | null;
		display_name: string;
		manual_avatar_url?: string | null;
		google_avatar_url?: string | null;
		rating?: number;
		loved?: boolean;
		must_see?: boolean;
		want_to_see?: boolean;
		saw_live?: boolean;
		saw_stream?: boolean;
		content?: string;
	}>;
	set?: ProcessedSet;
	festivalSlug?: string;
	variant?: "personal" | "public";
	isLoading?: boolean;
	size?: "sm" | "md";
	className?: string;
}

/**
 * Renders a facepile based on social interactions data
 */
export function renderFacepile({
	socialInteractions,
	set,
	festivalSlug,
	variant = "public",
	isLoading = false,
	size = "sm",
	className,
}: FacepileProps): ReactElement | null {
	// Don't show anything while loading or if no interactions
	if (isLoading || !socialInteractions || socialInteractions.length === 0) {
		return null;
	}

	// Hide facepile on personal profile pages
	if (variant === "personal") {
		return null;
	}

	return (
		<Facepile
			interactions={socialInteractions}
			set={set}
			festivalSlug={festivalSlug}
			size={size}
			className={className}
		/>
	);
}

// ===== DAY AND TIME FORMATTING =====

/**
 * Formats day and time in the format "Fri 4:00PM-5:00PM" or "Fri 16:00-17:00" based on user preferences
 * Uses the actual calendar day from start_time (not the adjusted day used for navigation)
 */
export function formatDayAndTime(
	set: ProcessedSet,
	userPreferences?: import("@workspace/types/src/entities/user").DateTimePreferences,
	showDay = true,
): string {
	// Check if this is a lineup-only set
	if (!set.start_time || !set.end_time) {
		return "Set Time: TBA";
	}

	// Use the festival programming day (set.day) instead of the calendar day from the timestamp.
	// This ensures overnight sets show the correct day (e.g., "Tue" for a 12:25AM set that's
	// part of Tuesday night's programming, even though the timestamp is technically Wednesday).
	const dayAbbreviationMap: Record<string, string> = {
		sunday: "Sun", monday: "Mon", tuesday: "Tue", wednesday: "Wed",
		thursday: "Thu", friday: "Fri", saturday: "Sat",
	};
	const dayAbbreviation = set.day ? dayAbbreviationMap[set.day] || "" : "";

	// Use preference-aware formatting if preferences are provided
	if (userPreferences) {
		const timeRange = formatSetTimeWithPreferences(
			set.start_time,
			set.end_time,
			userPreferences,
			false, // Don't include day since we're adding it separately
		);
		return showDay && dayAbbreviation ? `${dayAbbreviation} • ${timeRange}` : timeRange;
	}

	// Fallback to existing formatted time
	const fallbackTime = set.time || "TBA";
	return showDay && dayAbbreviation ? `${dayAbbreviation} • ${fallbackTime}` : fallbackTime;
}

// ===== STAGE FORMATTING =====

/**
 * Formats stage name with "Stage: TBA" fallback for lineup-only sets
 */
export function formatStageName(set: ProcessedSet): string {
	return set.stage?.name || "Stage: TBA";
}

/**
 * Formats stage name from string (used in SetPerformanceCard)
 */
export function formatStageNameFromString(
	stageName: string | null | undefined,
): string {
	return stageName || "Stage: TBA";
}
